import React, { useEffect, useState, useCallback, useMemo } from "react";
import { db, storage, auth } from "@/config/firebase";
import { collection, getDocs, doc, setDoc, getDoc, deleteDoc, onSnapshot, serverTimestamp, query, where, orderBy, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChallengeGameMaster } from "@/components/challenges/ChallengeGameMaster";
import { ArrowLeft, Upload, Music, Image, User, FileText, Play, Pause, Trash2, Plus, Save, X, Check, AlertCircle, Users, Sparkles, Settings, CheckCircle, AlertTriangle, XCircle, Search, ChevronDown, ChevronUp, Shield, UserCog } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CURRENT_SEASON = "2026-27";

// Status constants
const STATUS = {
  READY: 'READY',
  INCOMPLETE: 'INCOMPLETE',
  NEEDS_REVIEW: 'NEEDS_REVIEW',
};

// Prediction role options
const PREDICTION_ROLES = {
  superadmin: { label: 'Superadmin', color: 'bg-purple-600', description: 'Setup, Media, Settings, Rechte' },
  host: { label: 'Host', color: 'bg-amber-600', description: 'Live-Steuerung, Session-Control' },
  none: { label: 'Keine', color: 'bg-slate-600', description: 'Normaler Spieler' },
};

// Helper: Make authenticated API call with Firebase ID Token
async function apiCall(endpoint, method = 'GET', body = null) {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('Nicht eingeloggt');
  }
  
  const token = await currentUser.getIdToken();
  
  const options = {
    method,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };
  
  if (body && method !== 'GET') {
    options.body = JSON.stringify(body);
  }
  
  const response = await fetch(`${BACKEND_URL}${endpoint}`, options);
  const result = await response.json();
  
  if (!response.ok) {
    throw new Error(result.detail || `HTTP ${response.status}`);
  }
  
  return result;
}

// Helper: Get authenticated download URL for Firebase Storage
// Handles both direct URLs (from getDownloadURL) and storage paths
async function getAuthenticatedMediaUrl(urlOrPath) {
  if (!urlOrPath) return null;
  
  // If it's already a download URL with token, return as-is
  if (urlOrPath.includes('token=') || urlOrPath.includes('alt=media&token=')) {
    return urlOrPath;
  }
  
  // If it's a firebasestorage.googleapis.com URL without token, get the storage path
  if (urlOrPath.includes('firebasestorage.googleapis.com')) {
    try {
      // Extract path from URL: .../o/{encoded_path}?...
      const match = urlOrPath.match(/\/o\/([^?]+)/);
      if (match) {
        const storagePath = decodeURIComponent(match[1]);
        const storageRef = ref(storage, storagePath);
        return await getDownloadURL(storageRef);
      }
    } catch (err) {
      console.error('Failed to get download URL:', err);
      return urlOrPath; // Fallback to original
    }
  }
  
  // If it's a gs:// URL, extract path
  if (urlOrPath.startsWith('gs://')) {
    try {
      const path = urlOrPath.replace(/gs:\/\/[^\/]+\//, '');
      const storageRef = ref(storage, path);
      return await getDownloadURL(storageRef);
    } catch (err) {
      console.error('Failed to get download URL from gs://:', err);
      return urlOrPath;
    }
  }
  
  // Otherwise return as-is (might be an old Emergent URL or direct URL)
  return urlOrPath;
}

export default function PredictionsAdmin() {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  
  // Data
  const [users, setUsers] = useState([]);
  const [presentations, setPresentations] = useState({});
  const [predictions, setPredictions] = useState([]);
  const [imports, setImports] = useState([]);
  const [seasons, setSeasons] = useState([]);
  
  // UI State
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedImport, setSelectedImport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  // Portraits (chronik avatars)
  const [portraits, setPortraits] = useState([]);
  const [portraitPick, setPortraitPick] = useState({});
  const [loadingPortraits, setLoadingPortraits] = useState(false);
  const [savingPortrait, setSavingPortrait] = useState('');
  
  // Form State for selected user
  const [formData, setFormData] = useState({
    aliases: [],
    newAlias: '',
    onCourtPrediction: '',
    offCourtPrediction: '',
    onCourtId: null,
    offCourtId: null,
  });
  
  // Audio preview
  const [audioPlayer, setAudioPlayer] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // New User Creation
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [creatingUser, setCreatingUser] = useState(false);
  
  // Google Sheet Sync
  const [syncing, setSyncing] = useState(false);
  const GOOGLE_SHEET_ID = '17_49krvPYgAA5ZEQnHq5NKxttUrbNjc4UheSyMzrwBI';

  // Season settings (expanded with all coin economy fields)
  const [seasonSettings, setSeasonSettings] = useState({
    // Game timing
    guessSeconds: 30,           // WHO IS IT timer (max 30)
    // NOTE: Probability voting has NO time limit - auto-finalize when all eligible voters voted
    challengeBettingSeconds: 600, // Challenge betting timer (10 min default)
    
    // COIN ECONOMY - Phase A
    startingCoins: 100,         // Once per season per active participant
    correctGuessCoins: 10,      // Per correct WHO IS IT guess (self-guess ALLOWED)
    
    // COIN ECONOMY - Phase B (Challenges)
    challengeWinCoins: 20,      // Challenger bonus on SUCCESS
    
    // BETTING LIMITS
    challengeBetMin: 1,
    challengeBetMax: 500,
    predictionBetMin: 10,
    predictionBetMax: 1000,
    challengeMaxStakePercent: 50, // Max % of balance per challenge (50% default)
    
    // QUOTE CALCULATION
    quoteFloorProbability: 1,   // Minimum % for quote calc (prevents ÷0)
    maximumQuote: 100,          // Max quote for predictions
    challengeOddsSeed: 50,      // Seed for challenge live odds
    challengeMaximumQuote: 20,  // Max quote for challenges
  });
  
  // Role management state
  const [roleUsers, setRoleUsers] = useState([]);
  const [roleEditingUser, setRoleEditingUser] = useState(null);
  const [roleSaving, setRoleSaving] = useState(false);
  
  // Resolved media URLs (authenticated Firebase Storage URLs)
  const [resolvedMediaUrls, setResolvedMediaUrls] = useState({});

  // Load initial data
  useEffect(() => {
    if (role !== 'admin') return;
    
    const loadData = async () => {
      setLoading(true);
      try {
        // Load users
        const usersSnap = await getDocs(collection(db, "users"));
        const usersData = usersSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(u => !u.disabled && u.role !== 'sparring' && (u.display_name || u.name) && u.login_name !== 'spielmeister')
          .sort((a, b) => (a.display_name || a.name || '').localeCompare(b.display_name || b.name || ''));
        setUsers(usersData);
        
        // Load presentations for current season
        const presSnap = await getDocs(
          query(collection(db, "prediction_presentations"), where("seasonId", "==", CURRENT_SEASON))
        );
        const presData = {};
        presSnap.docs.forEach(d => {
          const data = d.data();
          presData[data.userId] = { id: d.id, ...data };
        });
        setPresentations(presData);
        
        // Load predictions for current season
        const predSnap = await getDocs(
          query(collection(db, "season_predictions"), where("seasonId", "==", CURRENT_SEASON))
        );
        setPredictions(predSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        
        // Load imports (unverified predictions)
        const importsSnap = await getDocs(
          query(collection(db, "prediction_imports"), where("seasonId", "==", CURRENT_SEASON))
        );
        setImports(importsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        
        // Load or create season (with expanded settings)
        const seasonDoc = await getDoc(doc(db, "prediction_seasons", CURRENT_SEASON));
        if (seasonDoc.exists()) {
          const seasonData = seasonDoc.data();
          setSeasonSettings(prev => ({ ...prev, ...seasonData.settings }));
        } else {
          await setDoc(doc(db, "prediction_seasons", CURRENT_SEASON), {
            name: "Season 2026/27",
            status: "setup",
            settings: seasonSettings,
            createdAt: serverTimestamp(),
          });
        }
        
        // Load users with prediction roles for role management
        setRoleUsers(usersData.map(u => ({
          ...u,
          predictionRole: u.predictionRole || (u.role === 'admin' ? 'superadmin' : 'none'),
          effectiveRole: u.predictionRole || (u.role === 'admin' ? 'superadmin' : 'none')
        })));
      } catch (err) {
        console.error("Error loading data:", err);
        showStatus('error', 'Fehler beim Laden der Daten');
      }
      setLoading(false);
    };
    
    loadData();
  }, [role]);

  // Load user data when selected
  useEffect(() => {
    if (!selectedUser) {
      setFormData({
        aliases: [],
        newAlias: '',
        onCourtPrediction: '',
        offCourtPrediction: '',
        onCourtId: null,
        offCourtId: null,
      });
      return;
    }
    
    const pres = presentations[selectedUser.id];
    const userPredictions = predictions.filter(p => p.ownerUserId === selectedUser.id);
    const onCourt = userPredictions.find(p => p.type === 'on_court');
    const offCourt = userPredictions.find(p => p.type === 'off_court');
    
    setFormData({
      aliases: pres?.aliases || generateDefaultAliases(selectedUser),
      newAlias: '',
      onCourtPrediction: onCourt?.text || '',
      offCourtPrediction: offCourt?.text || '',
      onCourtId: onCourt?.id || null,
      offCourtId: offCourt?.id || null,
    });
  }, [selectedUser, presentations, predictions]);

  // Generate default aliases from user data
  const generateDefaultAliases = (user) => {
    const aliases = new Set();
    const name = user.display_name || user.name || '';
    
    if (name) {
      aliases.add(name.toLowerCase().trim());
      const parts = name.split(' ');
      if (parts.length > 0) aliases.add(parts[0].toLowerCase().trim());
      if (parts.length > 1) aliases.add(parts[parts.length - 1].toLowerCase().trim());
    }
    
    if (user.login_name) aliases.add(user.login_name.toLowerCase().trim());
    
    return Array.from(aliases).filter(a => a.length > 0);
  };

  const showStatus = (type, message) => {
    setStatus({ type, message });
    setTimeout(() => setStatus({ type: '', message: '' }), 4000);
  };

  // Resolve Firebase Storage URLs when selectedUser changes
  useEffect(() => {
    const resolveUrls = async () => {
      if (!selectedUser) return;
      
      const pres = presentations[selectedUser.id];
      if (!pres) return;
      
      const key = selectedUser.id;
      
      // Check if already resolved
      if (resolvedMediaUrls[key]?.timestamp > Date.now() - 3600000) {
        return; // Use cached for 1 hour
      }
      
      try {
        const [bgUrl, songUrl] = await Promise.all([
          pres.backgroundImageUrl ? getAuthenticatedMediaUrl(pres.backgroundImageUrl) : null,
          pres.songUrl ? getAuthenticatedMediaUrl(pres.songUrl) : null,
        ]);
        
        setResolvedMediaUrls(prev => ({
          ...prev,
          [key]: {
            backgroundImageUrl: bgUrl,
            songUrl: songUrl,
            timestamp: Date.now(),
          }
        }));
      } catch (err) {
        console.error('Failed to resolve media URLs:', err);
      }
    };
    
    resolveUrls();
  }, [selectedUser, presentations]);
  
  // Get resolved URL or fallback to original
  const getResolvedUrl = useCallback((userId, type) => {
    const resolved = resolvedMediaUrls[userId];
    if (resolved && resolved[type]) {
      return resolved[type];
    }
    // Fallback to original URL from presentations
    const pres = presentations[userId];
    return type === 'backgroundImageUrl' ? pres?.backgroundImageUrl : pres?.songUrl;
  }, [resolvedMediaUrls, presentations]);

  // Calculate user status
  const getUserStatus = useCallback((userId) => {
    const pres = presentations[userId];
    const userPreds = predictions.filter(p => p.ownerUserId === userId);
    const hasOnCourt = userPreds.some(p => p.type === 'on_court');
    const hasOffCourt = userPreds.some(p => p.type === 'off_court');
    const hasImage = !!pres?.backgroundImageUrl;
    const hasSong = !!pres?.songUrl;
    const hasAliases = pres?.aliases?.length > 0;
    
    // Check if there's a pending import for this user
    const pendingImport = imports.find(i => 
      i.matchedUserId === userId && i.verificationStatus === 'pending'
    );
    
    if (pendingImport) {
      return STATUS.NEEDS_REVIEW;
    }
    
    if (hasOnCourt && hasOffCourt && hasImage && hasSong && hasAliases) {
      return STATUS.READY;
    }
    
    return STATUS.INCOMPLETE;
  }, [presentations, predictions, imports]);

  // Get status counts
  const statusCounts = useMemo(() => {
    const counts = { ready: 0, incomplete: 0, needsReview: 0 };
    users.forEach(u => {
      const status = getUserStatus(u.id);
      if (status === STATUS.READY) counts.ready++;
      else if (status === STATUS.NEEDS_REVIEW) counts.needsReview++;
      else counts.incomplete++;
    });
    return counts;
  }, [users, getUserStatus]);

  // Filter users by search
  const filteredUsers = useMemo(() => {
    if (!searchQuery) return users;
    const q = searchQuery.toLowerCase();
    return users.filter(u => {
      const name = (u.display_name || u.name || '').toLowerCase();
      return name.includes(q);
    });
  }, [users, searchQuery]);

  // Save presentation (aliases, media URLs are saved separately on upload)
  const savePresentation = async () => {
    if (!selectedUser) return;
    setSaving(true);
    
    // Include an alias still typed in the input (not yet confirmed with Enter/+)
    const pending = (formData.newAlias || '').toLowerCase().trim();
    const aliasList = [...formData.aliases.map(a => a.toLowerCase().trim()), ...(pending ? [pending] : [])]
      .filter((a, i, arr) => a && arr.indexOf(a) === i);
    
    try {
      const docId = `${CURRENT_SEASON}_${selectedUser.id}`;
      const existing = presentations[selectedUser.id] || {};
      
      await setDoc(doc(db, "prediction_presentations", docId), {
        seasonId: CURRENT_SEASON,
        userId: selectedUser.id,
        userDisplayName: selectedUser.display_name || selectedUser.name,
        aliases: aliasList,
        backgroundImageUrl: existing.backgroundImageUrl || null,
        songUrl: existing.songUrl || null,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      
      setFormData(prev => ({ ...prev, aliases: aliasList, newAlias: '' }));
      setPresentations(prev => ({
        ...prev,
        [selectedUser.id]: {
          ...existing,
          aliases: aliasList,
        }
      }));
      
      showStatus('success', `Aliases gespeichert (${aliasList.length})`);
    } catch (err) {
      console.error("Save presentation error:", err);
      showStatus('error', `Fehler beim Speichern: ${err?.message || err}`);
    }
    setSaving(false);
  };

  // Save predictions
  const savePredictions = async () => {
    if (!selectedUser) return;
    setSaving(true);
    
    try {
      if (formData.onCourtPrediction.trim()) {
        const onCourtDocId = formData.onCourtId || `${CURRENT_SEASON}_${selectedUser.id}_on_court`;
        await setDoc(doc(db, "season_predictions", onCourtDocId), {
          seasonId: CURRENT_SEASON,
          ownerUserId: selectedUser.id,
          ownerDisplayName: selectedUser.display_name || selectedUser.name,
          type: 'on_court',
          text: formData.onCourtPrediction.trim(),
          status: 'valid',
          teamProbability: null,
          quote: null,
          voteCount: 0,
          resultStatus: 'pending',
          updatedAt: serverTimestamp(),
        }, { merge: true });
      }
      
      if (formData.offCourtPrediction.trim()) {
        const offCourtDocId = formData.offCourtId || `${CURRENT_SEASON}_${selectedUser.id}_off_court`;
        await setDoc(doc(db, "season_predictions", offCourtDocId), {
          seasonId: CURRENT_SEASON,
          ownerUserId: selectedUser.id,
          ownerDisplayName: selectedUser.display_name || selectedUser.name,
          type: 'off_court',
          text: formData.offCourtPrediction.trim(),
          status: 'valid',
          teamProbability: null,
          quote: null,
          voteCount: 0,
          resultStatus: 'pending',
          updatedAt: serverTimestamp(),
        }, { merge: true });
      }
      
      // Reload predictions
      const predSnap = await getDocs(
        query(collection(db, "season_predictions"), where("seasonId", "==", CURRENT_SEASON))
      );
      setPredictions(predSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      
      showStatus('success', 'Predictions gespeichert');
    } catch (err) {
      console.error("Save predictions error:", err);
      showStatus('error', 'Fehler beim Speichern');
    }
    setSaving(false);
  };

  // Create new user via backend API
  const createNewUser = async () => {
    if (!newUserName.trim()) {
      showStatus('error', 'Bitte Namen eingeben');
      return;
    }
    
    setCreatingUser(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('Nicht eingeloggt');
      
      const token = await currentUser.getIdToken();
      const response = await fetch(`${BACKEND_URL}/api/admin/create-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          display_name: newUserName.trim(),
          password: '123wolle',
          role: 'player',
        }),
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || 'Fehler beim Erstellen');
      }
      
      // Reload users
      const usersResp = await fetch(`${BACKEND_URL}/api/users`);
      const usersData = await usersResp.json();
      setUsers(usersData);
      
      showStatus('success', `User "${result.display_name}" erstellt (${result.email})`);
      setNewUserName('');
      setShowCreateUser(false);
    } catch (err) {
      console.error("Create user error:", err);
      showStatus('error', err.message);
    }
    setCreatingUser(false);
  };

  // Sync predictions from Google Sheet
  const syncFromGoogleSheet = async () => {
    setSyncing(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('Nicht eingeloggt');
      
      const token = await currentUser.getIdToken();
      const response = await fetch(`${BACKEND_URL}/api/predictions/sync-google-sheet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          sheet_id: GOOGLE_SHEET_ID,
          season_id: CURRENT_SEASON,
        }),
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || 'Sync fehlgeschlagen');
      }
      
      // Reload imports
      const importsSnap = await getDocs(
        query(collection(db, "prediction_imports"), where("seasonId", "==", CURRENT_SEASON))
      );
      setImports(importsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      
      // Reload predictions (some may have been auto-matched)
      const predSnap = await getDocs(
        query(collection(db, "season_predictions"), where("seasonId", "==", CURRENT_SEASON))
      );
      setPredictions(predSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      
      showStatus('success', `${result.imported} neue, ${result.matched} auto-zugeordnet, ${result.skipped} übersprungen`);
    } catch (err) {
      console.error("Sync error:", err);
      showStatus('error', err.message);
    }
    setSyncing(false);
  };

  // Upload media file - NOW VIA BACKEND API (bypasses Firebase Storage rules)
  const uploadMedia = async (file, type) => {
    if (!selectedUser || !file) return;
    setSaving(true);
    
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Nicht eingeloggt');
      }
      
      const token = await currentUser.getIdToken();
      
      // Create FormData for file upload
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('user_id', selectedUser.id);
      uploadFormData.append('media_type', type);
      uploadFormData.append('season_id', CURRENT_SEASON);
      
      const response = await fetch(`${BACKEND_URL}/api/predictions/upload-media`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: uploadFormData,
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || `Upload failed: ${response.status}`);
      }
      
      // AUTO-SAVE ALIASES when uploading media (so user doesn't forget)
      const currentAliases = formData.aliases.map(a => a.toLowerCase().trim()).filter(a => a);
      if (currentAliases.length > 0) {
        const docId = `${CURRENT_SEASON}_${selectedUser.id}`;
        await setDoc(doc(db, "prediction_presentations", docId), {
          aliases: currentAliases,
        }, { merge: true });
      }
      
      // Update local state with the new URL
      const updateField = type === 'background' ? 'backgroundImageUrl' : 'songUrl';
      
      setPresentations(prev => ({
        ...prev,
        [selectedUser.id]: {
          ...prev[selectedUser.id],
          [updateField]: result.url,
          aliases: currentAliases.length > 0 ? currentAliases : prev[selectedUser.id]?.aliases,
        }
      }));
      
      showStatus('success', `${type === 'background' ? 'Bild' : 'Song'} hochgeladen`);
    } catch (err) {
      console.error("Upload error:", err);
      showStatus('error', `Upload fehlgeschlagen: ${err.message}`);
    }
    setSaving(false);
  };

  // Delete media
  const deleteMedia = async (type) => {
    if (!selectedUser) return;
    const pres = presentations[selectedUser.id];
    if (!pres) return;
    
    const url = type === 'background' ? pres.backgroundImageUrl : pres.songUrl;
    if (!url) return;
    
    setSaving(true);
    try {
      try {
        const storageRef = ref(storage, url);
        await deleteObject(storageRef);
      } catch (e) {
        // File might not exist
      }
      
      const docId = `${CURRENT_SEASON}_${selectedUser.id}`;
      const updateField = type === 'background' ? 'backgroundImageUrl' : 'songUrl';
      
      await setDoc(doc(db, "prediction_presentations", docId), {
        [updateField]: null,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      
      setPresentations(prev => ({
        ...prev,
        [selectedUser.id]: {
          ...prev[selectedUser.id],
          [updateField]: null,
        }
      }));
      
      showStatus('success', `${type === 'background' ? 'Bild' : 'Song'} gelöscht`);
    } catch (err) {
      console.error("Delete error:", err);
      showStatus('error', 'Löschen fehlgeschlagen');
    }
    setSaving(false);
  };

  // Audio controls
  const toggleAudio = (url) => {
    if (!url) return;
    
    if (audioPlayer && isPlaying) {
      audioPlayer.pause();
      setIsPlaying(false);
    } else {
      if (audioPlayer) {
        audioPlayer.src = url;
        audioPlayer.play();
      } else {
        const audio = new Audio(url);
        audio.onended = () => setIsPlaying(false);
        audio.play();
        setAudioPlayer(audio);
      }
      setIsPlaying(true);
    }
  };

  // Stop audio when changing user
  useEffect(() => {
    if (audioPlayer) {
      audioPlayer.pause();
      setAudioPlayer(null);
      setIsPlaying(false);
    }
  }, [selectedUser]);

  // Add alias
  const addAlias = () => {
    const alias = formData.newAlias.toLowerCase().trim();
    if (alias && !formData.aliases.includes(alias)) {
      setFormData(prev => ({
        ...prev,
        aliases: [...prev.aliases, alias],
        newAlias: '',
      }));
    }
  };

  // Remove alias
  const removeAlias = (alias) => {
    setFormData(prev => ({
      ...prev,
      aliases: prev.aliases.filter(a => a !== alias),
    }));
  };

  // ============================================
  // IMPORT VERIFICATION FUNCTIONS
  // ============================================
  
  // Match import to user
  const findBestMatch = (formName) => {
    const normalized = formName.toLowerCase().trim();
    
    // Alias match (from prediction_presentations, e.g. "Gigi" → Guido Schild)
    const aliasHit = Object.values(presentations).find(p =>
      (p.aliases || []).some(a => String(a).toLowerCase().trim() === normalized)
    );
    if (aliasHit) {
      const aliasUser = users.find(u => u.id === aliasHit.userId);
      if (aliasUser) return { user: aliasUser, confidence: 'high' };
    }
    
    // Exact match first
    let match = users.find(u => {
      const displayName = (u.display_name || '').toLowerCase();
      const name = (u.name || '').toLowerCase();
      const loginName = (u.login_name || '').toLowerCase();
      return displayName === normalized || name === normalized || loginName === normalized;
    });
    
    if (match) return { user: match, confidence: 'high' };
    
    // Partial match
    match = users.find(u => {
      const displayName = (u.display_name || '').toLowerCase();
      const name = (u.name || '').toLowerCase();
      return displayName.includes(normalized) || normalized.includes(displayName.split(' ')[0]) ||
             name.includes(normalized) || normalized.includes(name.split(' ')[0]);
    });
    
    if (match) return { user: match, confidence: 'medium' };
    
    return { user: null, confidence: 'none' };
  };

  // Verify import and create predictions
  const verifyImport = async (importItem, userId, onCourtText, offCourtText, mergedImportIds = []) => {
    if (!userId) {
      showStatus('error', 'Bitte einen User auswählen');
      return;
    }
    if (!onCourtText?.trim() && !offCourtText?.trim()) {
      showStatus('error', 'Mindestens eine Prediction (ON oder OFF) muss Text haben');
      return;
    }
    
    setSaving(true);
    try {
      const targetUser = users.find(u => u.id === userId);
      if (!targetUser) throw new Error('User nicht gefunden');
      
      // Save ON-COURT prediction
      if (onCourtText?.trim()) {
        const onCourtDocId = `${CURRENT_SEASON}_${userId}_on_court`;
        await setDoc(doc(db, "season_predictions", onCourtDocId), {
          seasonId: CURRENT_SEASON,
          ownerUserId: userId,
          ownerDisplayName: targetUser.display_name || targetUser.name,
          type: 'on_court',
          text: onCourtText.trim(),
          status: 'valid',
          teamProbability: null,
          quote: null,
          voteCount: 0,
          resultStatus: 'pending',
          createdAt: serverTimestamp(),
        }, { merge: true });
      }
      
      // Save OFF-COURT prediction
      if (offCourtText?.trim()) {
        const offCourtDocId = `${CURRENT_SEASON}_${userId}_off_court`;
        await setDoc(doc(db, "season_predictions", offCourtDocId), {
          seasonId: CURRENT_SEASON,
          ownerUserId: userId,
          ownerDisplayName: targetUser.display_name || targetUser.name,
          type: 'off_court',
          text: offCourtText.trim(),
          status: 'valid',
          teamProbability: null,
          quote: null,
          voteCount: 0,
          resultStatus: 'pending',
          createdAt: serverTimestamp(),
        }, { merge: true });
      }
      
      // Create/update presentation with default aliases
      const presDocId = `${CURRENT_SEASON}_${userId}`;
      const existingPres = presentations[userId];
      if (!existingPres) {
        await setDoc(doc(db, "prediction_presentations", presDocId), {
          seasonId: CURRENT_SEASON,
          userId: userId,
          userDisplayName: targetUser.display_name || targetUser.name,
          aliases: generateDefaultAliases(targetUser),
          backgroundImageUrl: null,
          songUrl: null,
          createdAt: serverTimestamp(),
        });
      }
      
      // Mark import as verified
      await setDoc(doc(db, "prediction_imports", importItem.id), {
        matchedUserId: userId,
        matchedUserName: targetUser.display_name || targetUser.name,
        status: 'verified',
        verificationStatus: 'verified',
        onCourtVerified: !!onCourtText?.trim(),
        offCourtVerified: !!offCourtText?.trim(),
        verifiedAt: serverTimestamp(),
      }, { merge: true });
      
      // Mark merged duplicate imports (same author) as verified too
      for (const mid of mergedImportIds) {
        await setDoc(doc(db, "prediction_imports", mid), {
          matchedUserId: userId,
          matchedUserName: targetUser.display_name || targetUser.name,
          status: 'verified',
          verificationStatus: 'verified',
          mergedInto: importItem.id,
          verifiedAt: serverTimestamp(),
        }, { merge: true });
      }
      
      // Reload data
      const [predSnap, presSnap, importsSnap] = await Promise.all([
        getDocs(query(collection(db, "season_predictions"), where("seasonId", "==", CURRENT_SEASON))),
        getDocs(query(collection(db, "prediction_presentations"), where("seasonId", "==", CURRENT_SEASON))),
        getDocs(query(collection(db, "prediction_imports"), where("seasonId", "==", CURRENT_SEASON))),
      ]);
      
      setPredictions(predSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const presData = {};
      presSnap.docs.forEach(d => {
        const data = d.data();
        presData[data.userId] = { id: d.id, ...data };
      });
      setPresentations(presData);
      setImports(importsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      
      setSelectedImport(null);
      showStatus('success', 'Import verifiziert und Predictions erstellt');
    } catch (err) {
      console.error("Verify import error:", err);
      showStatus('error', 'Fehler bei der Verifizierung');
    }
    setSaving(false);
  };

  // Reject import
  const rejectImport = async (importItem) => {
    setSaving(true);
    try {
      await setDoc(doc(db, "prediction_imports", importItem.id), {
        status: 'rejected',
        verificationStatus: 'rejected',
        rejectedAt: serverTimestamp(),
      }, { merge: true });
      
      setImports(prev => prev.map(i => 
        i.id === importItem.id ? { ...i, status: 'rejected', verificationStatus: 'rejected' } : i
      ));
      setSelectedImport(null);
      showStatus('success', 'Import abgelehnt');
    } catch (err) {
      showStatus('error', 'Fehler beim Ablehnen');
    }
    setSaving(false);
  };

  // Save season settings
  const saveSeasonSettings = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, "prediction_seasons", CURRENT_SEASON), {
        settings: seasonSettings,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      showStatus('success', 'Einstellungen gespeichert');
    } catch (err) {
      showStatus('error', 'Fehler beim Speichern');
    }
    setSaving(false);
  };
  
  // Set prediction role for a user (superadmin only)
  const setPredictionRole = async (targetUserId, newRole) => {
    setRoleSaving(true);
    try {
      // Use backend API with token auth
      const result = await apiCall('/api/predictions/set-role', 'POST', {
        target_user_id: targetUserId,
        role: newRole,
      });
      
      if (result.ok) {
        // Update local state
        setRoleUsers(prev => prev.map(u => 
          u.id === targetUserId 
            ? { ...u, predictionRole: newRole, effectiveRole: newRole }
            : u
        ));
        showStatus('success', `${result.display_name}: ${result.old_role} → ${result.new_role}`);
      }
    } catch (err) {
      console.error('Set role error:', err);
      showStatus('error', err.message || 'Fehler beim Setzen der Rolle');
    }
    setRoleSaving(false);
    setRoleEditingUser(null);
  };

  // ── Portraits (chronik avatars) ──────────────────────────────
  const loadPortraits = useCallback(async () => {
    if (!user?.uid) return;
    setLoadingPortraits(true);
    try {
      const res = await apiCall(`/api/admin/portraits?admin_id=${user.uid}`);
      const list = res.portraits || [];
      setPortraits(list);
      // preselect: user currently holding this url, else suggested
      setPortraitPick(prev => {
        const next = { ...prev };
        list.forEach(p => {
          if (next[p.nick]) return;
          const holder = users.find(u => u.photo_url === p.url);
          next[p.nick] = holder?.id || p.suggested_user_id || '';
        });
        return next;
      });
    } catch (err) {
      showStatus('error', err.message || 'Portraits laden fehlgeschlagen');
    }
    setLoadingPortraits(false);
  }, [user, users]);

  useEffect(() => {
    if (activeTab === 'portraits') loadPortraits();
  }, [activeTab, loadPortraits]);

  const assignPortrait = async (nick, url) => {
    const targetId = portraitPick[nick];
    if (!targetId) { showStatus('error', 'Bitte Spieler auswählen'); return; }
    setSavingPortrait(nick);
    try {
      await apiCall(`/api/admin/users/${targetId}?admin_id=${user.uid}`, 'PUT', { photo_url: url });
      setUsers(prev => prev.map(u => u.id === targetId ? { ...u, photo_url: url } : u));
      const tName = users.find(u => u.id === targetId);
      showStatus('success', `${nick} → ${tName?.display_name || tName?.name || 'Spieler'}`);
    } catch (err) {
      showStatus('error', err.message || 'Zuordnung fehlgeschlagen');
    }
    setSavingPortrait('');
  };

  const clearPortrait = async (targetId) => {
    if (!targetId) return;
    setSavingPortrait('clear-' + targetId);
    try {
      await apiCall(`/api/admin/users/${targetId}?admin_id=${user.uid}`, 'PUT', { photo_url: '' });
      setUsers(prev => prev.map(u => u.id === targetId ? { ...u, photo_url: '' } : u));
      showStatus('success', 'Portrait entfernt');
    } catch (err) {
      showStatus('error', err.message || 'Entfernen fehlgeschlagen');
    }
    setSavingPortrait('');
  };

  // Render status badge
  const StatusBadge = ({ status }) => {
    switch (status) {
      case STATUS.READY:
        return (
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs">
            <CheckCircle size={12} /> READY
          </span>
        );
      case STATUS.NEEDS_REVIEW:
        return (
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-xs">
            <AlertTriangle size={12} /> REVIEW
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-500/20 text-gray-400 text-xs">
            <XCircle size={12} /> INCOMPLETE
          </span>
        );
    }
  };

  if (role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0a0608] text-rose-50 flex items-center justify-center">
        <p>Kein Zugriff - nur für Admins</p>
      </div>
    );
  }

  const currentPres = presentations[selectedUser?.id];
  const isImportPending = (i) => {
    const vs = i.verificationStatus;
    if (vs === 'verified' || vs === 'rejected') return false;
    return i.status === 'pending' || vs === 'pending';
  };
  const pendingImports = imports.filter(isImportPending);

  return (
    <div className="min-h-screen bg-[#0a0608] text-rose-50 pb-24">
      {/* Header */}
      <header className="px-4 py-3 bg-[#12090c] border-b border-white/5 sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/admin')} className="p-1">
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1">
            <h1 className="font-semibold flex items-center gap-2">
              <Sparkles size={18} className="text-amber-400" />
              Predictions Admin
            </h1>
            <p className="text-xs text-rose-200/50">Season {CURRENT_SEASON}</p>
          </div>
          <Button
            onClick={() => navigate('/admin/predictions/sessions')}
            className="bg-green-600 hover:bg-green-700 text-sm"
            data-testid="sessions-btn"
          >
            Sessions
          </Button>
        </div>
      </header>

      {/* Status Toast */}
      {status.message && (
        <div className={`mx-4 mt-4 p-3 rounded-lg text-sm flex items-center gap-2 ${
          status.type === 'error' ? 'bg-red-900/50 border border-red-500/50' : 'bg-green-900/50 border border-green-500/50'
        }`}>
          {status.type === 'error' ? <AlertCircle size={16} /> : <Check size={16} />}
          {status.message}
        </div>
      )}

      {/* Status Overview */}
      <div className="px-4 py-4">
        <Card className="bg-[#12090c] border-white/10">
          <CardContent className="pt-4">
            <div className="grid grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-green-400">{statusCounts.ready}</p>
                <p className="text-xs text-rose-200/50">READY</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-400">{statusCounts.needsReview}</p>
                <p className="text-xs text-rose-200/50">REVIEW</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-400">{statusCounts.incomplete}</p>
                <p className="text-xs text-rose-200/50">INCOMPLETE</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-400">{pendingImports.length}</p>
                <p className="text-xs text-rose-200/50">PENDING</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="px-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-testid="predictions-admin-tabs">
          <TabsList className="w-full bg-[#12090c] border border-white/5 mb-4">
            <TabsTrigger value="overview" className="flex-1 text-xs" data-testid="tab-overview">
              Übersicht
            </TabsTrigger>
            <TabsTrigger value="verify" className="flex-1 text-xs relative" data-testid="tab-verify">
              Verifizierung
              {pendingImports.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 rounded-full text-[10px] flex items-center justify-center">
                  {pendingImports.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="economy" className="flex-1 text-xs" data-testid="tab-economy">
              Economy
            </TabsTrigger>
            <TabsTrigger value="market" className="flex-1 text-xs" data-testid="tab-market">
              Markt
            </TabsTrigger>
            <TabsTrigger value="roles" className="flex-1 text-xs" data-testid="tab-roles">
              Rollen
            </TabsTrigger>
            <TabsTrigger value="portraits" className="flex-1 text-xs" data-testid="tab-portraits">
              Portraits
            </TabsTrigger>
            <TabsTrigger value="games" className="flex-1 text-xs" data-testid="tab-games">
              Mini-Games
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex-1 text-xs" data-testid="tab-settings">
              Einstellungen
            </TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-rose-200/40" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Spieler suchen..."
                className="pl-10 bg-white/5 border-white/10"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* User List */}
              <Card className="bg-[#12090c] border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Users size={16} />
                      Spieler ({filteredUsers.length})
                    </span>
                    <Button 
                      size="sm" 
                      onClick={() => setShowCreateUser(true)}
                      className="bg-green-600 hover:bg-green-700 text-xs h-7"
                    >
                      <Plus size={12} className="mr-1" />
                      Neu
                    </Button>
                  </CardTitle>
                  
                  {/* Create User Dialog */}
                  {showCreateUser && (
                    <div className="mt-3 p-3 bg-green-900/20 border border-green-500/30 rounded-lg space-y-2">
                      <p className="text-xs text-green-400 font-medium">Neuen Spieler anlegen:</p>
                      <Input
                        value={newUserName}
                        onChange={(e) => setNewUserName(e.target.value)}
                        placeholder="Name (z.B. Bastian Stein)"
                        className="bg-white/10 border-white/20 text-sm h-8"
                        onKeyDown={(e) => e.key === 'Enter' && createNewUser()}
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <Button 
                          onClick={createNewUser} 
                          size="sm" 
                          className="flex-1 bg-green-600 hover:bg-green-700 h-7 text-xs"
                          disabled={creatingUser || !newUserName.trim()}
                        >
                          {creatingUser ? 'Erstelle...' : 'Erstellen'}
                        </Button>
                        <Button 
                          onClick={() => {setShowCreateUser(false); setNewUserName('');}} 
                          size="sm" 
                          variant="outline" 
                          className="h-7 text-xs border-white/20"
                        >
                          Abbrechen
                        </Button>
                      </div>
                      <p className="text-[10px] text-rose-200/40">Passwort: 123wolle</p>
                    </div>
                  )}
                </CardHeader>
                <CardContent className="max-h-[600px] overflow-y-auto space-y-1">
                  {loading ? (
                    <p className="text-sm text-rose-200/50">Laden...</p>
                  ) : (
                    filteredUsers.map(u => {
                      const pres = presentations[u.id];
                      const userPreds = predictions.filter(p => p.ownerUserId === u.id);
                      const hasOnCourt = userPreds.some(p => p.type === 'on_court');
                      const hasOffCourt = userPreds.some(p => p.type === 'off_court');
                      const hasImage = !!pres?.backgroundImageUrl;
                      const hasSong = !!pres?.songUrl;
                      const hasAliases = pres?.aliases?.length > 0;
                      const status = getUserStatus(u.id);
                      
                      return (
                        <button
                          key={u.id}
                          onClick={() => setSelectedUser(u)}
                          data-testid={`player-btn-${u.id}`}
                          className={`w-full text-left p-2 rounded-lg transition-colors ${
                            selectedUser?.id === u.id 
                              ? 'bg-rose-600/30 border border-rose-500/50' 
                              : 'bg-white/5 hover:bg-white/10 border border-transparent'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium truncate">
                              {u.display_name || u.name || u.email}
                            </span>
                            <StatusBadge status={status} />
                          </div>
                          <div className="flex items-center gap-2 text-[10px] text-rose-200/50">
                            <span className={hasOnCourt ? 'text-green-400' : ''}>
                              {hasOnCourt ? '✅' : '❌'} ON
                            </span>
                            <span className={hasOffCourt ? 'text-green-400' : ''}>
                              {hasOffCourt ? '✅' : '❌'} OFF
                            </span>
                            <span className={hasImage ? 'text-green-400' : ''}>
                              {hasImage ? '✅' : '❌'} BG
                            </span>
                            <span className={hasSong ? 'text-green-400' : ''}>
                              {hasSong ? '✅' : '❌'} 🎵
                            </span>
                            <span className={hasAliases ? 'text-green-400' : ''}>
                              {hasAliases ? '✅' : '❌'} Alias
                            </span>
                          </div>
                        </button>
                      );
                    })
                  )}
                </CardContent>
              </Card>

              {/* User Editor */}
              {selectedUser ? (
                <Card className="bg-[#12090c] border-white/10 lg:col-span-2">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <User size={16} />
                      <span className="text-amber-400 font-bold">Predictions von:</span>
                      {selectedUser.display_name || selectedUser.name}
                      <StatusBadge status={getUserStatus(selectedUser.id)} />
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Aliases */}
                    <div>
                      <label className="text-xs text-rose-200/60 block mb-2">
                        Aliases (für Name-Erkennung)
                      </label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {formData.aliases.map(alias => (
                          <span key={alias} className="px-2 py-1 bg-white/10 rounded-lg text-sm flex items-center gap-1">
                            {alias}
                            <button onClick={() => removeAlias(alias)} className="text-rose-400 hover:text-rose-300">
                              <X size={12} />
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Input
                          data-testid="alias-input"
                          value={formData.newAlias}
                          onChange={(e) => setFormData(prev => ({ ...prev, newAlias: e.target.value }))}
                          onBlur={addAlias}
                          placeholder="Neuen Alias hinzufügen… (Enter oder +)"
                          className="bg-white/5 border-white/10 text-sm"
                          onKeyDown={(e) => (e.key === 'Enter' || e.key === ',') && (e.preventDefault(), addAlias())}
                        />
                        <Button data-testid="alias-add-btn" onClick={addAlias} size="sm" className="bg-white/10 hover:bg-white/20">
                          <Plus size={14} />
                        </Button>
                      </div>
                      <Button data-testid="alias-save-btn" onClick={savePresentation} disabled={saving} size="sm" className="mt-2 bg-blue-600 hover:bg-blue-700">
                        <Save size={14} className="mr-1" /> Aliases speichern
                      </Button>
                    </div>

                    {/* Predictions - These are predictions BY this user (author), not ABOUT this user */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-rose-200/60 block mb-2 flex items-center gap-1">
                          <span className="w-3 h-3 bg-blue-500 rounded" />
                          ON-COURT Prediction <span className="text-amber-400">(von {selectedUser.display_name?.split(' ')[0] || 'User'})</span>
                        </label>
                        <textarea
                          value={formData.onCourtPrediction}
                          onChange={(e) => setFormData(prev => ({ ...prev, onCourtPrediction: e.target.value }))}
                          placeholder="Basketball-bezogene Prediction..."
                          className="w-full p-2 bg-white/5 border border-white/10 rounded-lg text-sm min-h-[80px] resize-none"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-rose-200/60 block mb-2 flex items-center gap-1">
                          <span className="w-3 h-3 bg-purple-500 rounded" />
                          OFF-COURT Prediction <span className="text-amber-400">(von {selectedUser.display_name?.split(' ')[0] || 'User'})</span>
                        </label>
                        <textarea
                          value={formData.offCourtPrediction}
                          onChange={(e) => setFormData(prev => ({ ...prev, offCourtPrediction: e.target.value }))}
                          placeholder="Team/Social Prediction..."
                          className="w-full p-2 bg-white/5 border border-white/10 rounded-lg text-sm min-h-[80px] resize-none"
                        />
                      </div>
                    </div>
                    <Button onClick={savePredictions} disabled={saving} className="w-full bg-green-600 hover:bg-green-700">
                      <Save size={14} className="mr-2" /> Predictions speichern
                    </Button>

                    {/* Media Upload */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-white/10">
                      {/* Background Image */}
                      <div>
                        <label className="text-xs text-rose-200/60 block mb-2 flex items-center gap-1">
                          <Image size={14} />
                          Background Bild
                        </label>
                        {currentPres?.backgroundImageUrl ? (
                          <div className="space-y-2">
                            <img 
                              src={getResolvedUrl(selectedUser?.id, 'backgroundImageUrl') || currentPres.backgroundImageUrl} 
                              alt="Background" 
                              className="w-full h-32 object-cover rounded-lg"
                            />
                            <div className="flex gap-2">
                              <label className="flex-1">
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => e.target.files?.[0] && uploadMedia(e.target.files[0], 'background')}
                                  className="hidden"
                                />
                                <Button variant="outline" size="sm" className="w-full bg-white/5 border-white/10" asChild>
                                  <span><Upload size={14} className="mr-1" /> Ersetzen</span>
                                </Button>
                              </label>
                              <Button 
                                onClick={() => deleteMedia('background')} 
                                size="sm" 
                                className="bg-red-600/20 hover:bg-red-600/30 text-red-300"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <label className="block">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => e.target.files?.[0] && uploadMedia(e.target.files[0], 'background')}
                              className="hidden"
                            />
                            <div className="w-full h-32 border-2 border-dashed border-white/20 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-white/40 transition-colors">
                              <Upload size={24} className="text-rose-200/40 mb-2" />
                              <span className="text-xs text-rose-200/50">Bild hochladen</span>
                              <span className="text-[10px] text-rose-200/30">JPG, PNG, WebP</span>
                            </div>
                          </label>
                        )}
                      </div>

                      {/* Song */}
                      <div>
                        <label className="text-xs text-rose-200/60 block mb-2 flex items-center gap-1">
                          <Music size={14} />
                          Song
                        </label>
                        {currentPres?.songUrl ? (
                          <div className="space-y-2">
                            <div className="w-full h-32 bg-gradient-to-br from-amber-900/30 to-orange-900/30 rounded-lg flex flex-col items-center justify-center">
                              <Music size={32} className="text-amber-400 mb-2" />
                              <Button onClick={() => toggleAudio(getResolvedUrl(selectedUser?.id, 'songUrl') || currentPres.songUrl)} size="sm" className="bg-amber-600 hover:bg-amber-700">
                                {isPlaying ? <Pause size={14} className="mr-1" /> : <Play size={14} className="mr-1" />}
                                {isPlaying ? 'Pause' : 'Preview'}
                              </Button>
                            </div>
                            <div className="flex gap-2">
                              <label className="flex-1">
                                <input
                                  type="file"
                                  accept="audio/*"
                                  onChange={(e) => e.target.files?.[0] && uploadMedia(e.target.files[0], 'audio')}
                                  className="hidden"
                                />
                                <Button variant="outline" size="sm" className="w-full bg-white/5 border-white/10" asChild>
                                  <span><Upload size={14} className="mr-1" /> Ersetzen</span>
                                </Button>
                              </label>
                              <Button 
                                onClick={() => deleteMedia('audio')} 
                                size="sm" 
                                className="bg-red-600/20 hover:bg-red-600/30 text-red-300"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <label className="block">
                            <input
                              type="file"
                              accept="audio/*"
                              onChange={(e) => e.target.files?.[0] && uploadMedia(e.target.files[0], 'audio')}
                              className="hidden"
                            />
                            <div className="w-full h-32 border-2 border-dashed border-white/20 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-white/40 transition-colors">
                              <Music size={24} className="text-rose-200/40 mb-2" />
                              <span className="text-xs text-rose-200/50">Song hochladen</span>
                              <span className="text-[10px] text-rose-200/30">MP3, M4A, WAV</span>
                            </div>
                          </label>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-[#12090c] border-white/10 lg:col-span-2 flex items-center justify-center min-h-[400px]">
                  <div className="text-center text-rose-200/40">
                    <User size={48} className="mx-auto mb-3 opacity-30" />
                    <p>Wähle einen Spieler aus der Liste</p>
                  </div>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* VERIFICATION TAB */}
          <TabsContent value="verify" className="space-y-4">
            <Card className="bg-[#12090c] border-white/10">
              <CardHeader>
                <CardTitle className="text-sm flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <FileText size={16} />
                    Importierte Predictions ({pendingImports.length} ausstehend)
                  </span>
                  <Button
                    size="sm"
                    onClick={syncFromGoogleSheet}
                    disabled={syncing}
                    className="bg-green-600 hover:bg-green-700 text-xs h-7"
                  >
                    {syncing ? (
                      <>Sync läuft...</>
                    ) : (
                      <><Upload size={12} className="mr-1" /> Google Sheet Sync</>
                    )}
                  </Button>
                </CardTitle>
                <p className="text-xs text-rose-200/50 mt-1">
                  Spalte B = AUTOR der Prediction → Welcher App-User hat das eingereicht?
                </p>
              </CardHeader>
              <CardContent>
                {pendingImports.length === 0 ? (
                  <div className="text-center py-8 text-rose-200/50">
                    <CheckCircle size={48} className="mx-auto mb-3 opacity-30" />
                    <p>Alle Imports verifiziert!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingImports.map(imp => {
                      const match = findBestMatch(imp.formName);
                      const isSelected = selectedImport?.id === imp.id;
                      
                      return (
                        <div 
                          key={imp.id}
                          className={`p-4 rounded-lg border transition-colors ${
                            isSelected 
                              ? 'bg-rose-600/20 border-rose-500/50' 
                              : 'bg-white/5 border-white/10 hover:bg-white/10'
                          }`}
                        >
                          <div 
                            className="cursor-pointer"
                            onClick={() => setSelectedImport(isSelected ? null : imp)}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-amber-400 font-medium">AUTOR:</span>
                                <span className="font-bold text-lg">{imp.formName}</span>
                                {match.confidence === 'high' && (
                                  <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full">
                                    → {match.user?.display_name || match.user?.name}
                                  </span>
                                )}
                                {match.confidence === 'medium' && (
                                  <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded-full">
                                    → {match.user?.display_name || match.user?.name}?
                                  </span>
                                )}
                                {match.confidence === 'none' && (
                                  <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full">
                                    ⚠️ Kein Match
                                  </span>
                                )}
                              </div>
                              {isSelected ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2 text-xs text-rose-200/60">
                              <div>
                                <span className="text-blue-400">ON:</span> {imp.onCourtText?.trim() ? `${imp.onCourtText.substring(0, 50)}...` : <span className="italic text-rose-300/50">(leer)</span>}
                              </div>
                              <div>
                                <span className="text-purple-400">OFF:</span> {imp.offCourtText?.trim() ? `${imp.offCourtText.substring(0, 50)}...` : <span className="italic text-rose-300/50">(leer)</span>}
                              </div>
                            </div>
                          </div>
                          
                          {/* Expanded verification form */}
                          {isSelected && (
                            <ImportVerificationForm 
                              importItem={imp}
                              suggestedUser={match.user}
                              users={users}
                              siblingImports={pendingImports.filter(o => o.id !== imp.id && (o.formName || '').toLowerCase().trim() === (imp.formName || '').toLowerCase().trim())}
                              onVerify={verifyImport}
                              onReject={rejectImport}
                              saving={saving}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Already verified - Show ALL predictions from season_predictions grouped by author */}
            {predictions.length > 0 && (
              <Card className="bg-[#12090c] border-white/10">
                <CardHeader>
                  <CardTitle className="text-sm text-green-400">
                    ✅ Verifiziert ({[...new Set(predictions.map(p => p.ownerUserId))].length} Spieler)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {/* Group predictions by owner and show unique owners */}
                    {[...new Set(predictions.map(p => p.ownerUserId))].map(userId => {
                      const userPreds = predictions.filter(p => p.ownerUserId === userId);
                      const displayName = userPreds[0]?.ownerDisplayName || 'Unknown';
                      const hasOnCourt = userPreds.some(p => p.type === 'on_court');
                      const hasOffCourt = userPreds.some(p => p.type === 'off_court');
                      
                      return (
                        <div key={userId} className="p-2 bg-green-900/20 rounded-lg text-sm">
                          <span className="font-medium">{displayName}</span>
                          <div className="flex gap-1 mt-1">
                            {hasOnCourt && <span className="text-[10px] px-1 bg-blue-500/30 text-blue-300 rounded">ON</span>}
                            {hasOffCourt && <span className="text-[10px] px-1 bg-purple-500/30 text-purple-300 rounded">OFF</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ROLES TAB */}
          <TabsContent value="roles" className="space-y-4">
            <Card className="bg-[#12090c] border-white/10">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Shield size={16} />
                  Prediction Rollen verwalten
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-xs text-rose-200/50 mb-4">
                  Superadmins können Setup, Media und Einstellungen verwalten. Hosts können Live-Sessions steuern.
                </p>
                
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {roleUsers.map(u => {
                    const roleInfo = PREDICTION_ROLES[u.effectiveRole] || PREDICTION_ROLES.none;
                    const isEditing = roleEditingUser === u.id;
                    
                    return (
                      <div 
                        key={u.id}
                        className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-rose-600/30 rounded-full flex items-center justify-center">
                            <UserCog size={14} />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{u.display_name || u.name || u.email}</p>
                            <p className="text-xs text-rose-200/40">
                              {u.role === 'admin' && u.predictionRole !== u.effectiveRole && '(App Admin) '}
                              {roleInfo.description}
                            </p>
                          </div>
                        </div>
                        
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            {Object.entries(PREDICTION_ROLES).map(([role, info]) => (
                              <button
                                key={role}
                                onClick={() => setPredictionRole(u.id, role)}
                                disabled={roleSaving}
                                className={`px-2 py-1 rounded text-xs ${info.color} hover:opacity-80 transition-opacity`}
                              >
                                {info.label}
                              </button>
                            ))}
                            <button
                              onClick={() => setRoleEditingUser(null)}
                              className="p-1 text-rose-200/40 hover:text-white"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setRoleEditingUser(u.id)}
                            className={`px-3 py-1 rounded-full text-xs ${roleInfo.color}`}
                          >
                            {roleInfo.label}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ECONOMY TAB */}
          <TabsContent value="economy" className="space-y-4">
            <EconomyOverviewTab />
          </TabsContent>

          {/* SEASON MARKET TAB */}
          <TabsContent value="market" className="space-y-4">
            <SeasonMarketAdminTab />
          </TabsContent>

          {/* MINI-GAMES TAB (Katalog: ansehen / anlegen / bearbeiten / duplizieren / löschen) */}
          <TabsContent value="games" className="space-y-4">
            <Card className="bg-[#12090c] border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><Sparkles size={16} /> Mini-Games verwalten</CardTitle>
                <p className="text-xs text-rose-200/50">Games ansehen, neue anlegen, bearbeiten, duplizieren oder pausieren – schon bevor eine Session läuft. Welche Games heute ins Rad kommen, wählst du beim Session-Start in der Lobby (🟢 IM RAD / ⚫ HEUTE RAUS).</p>
              </CardHeader>
              <CardContent>
                <ChallengeGameMaster apiCall={apiCall} catalogOnly />
              </CardContent>
            </Card>
          </TabsContent>

          {/* PORTRAITS TAB */}
          <TabsContent value="portraits" className="space-y-4">
            <Card className="bg-[#12090c] border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center justify-between">
                  <span className="flex items-center gap-2"><User size={16} /> Chronik-Portraits zuordnen</span>
                  <Button size="sm" variant="outline" className="h-7 text-xs border-white/20" onClick={loadPortraits} data-testid="portraits-reload">
                    Neu laden
                  </Button>
                </CardTitle>
                <p className="text-[11px] text-rose-200/50 mt-1">
                  Freigestellte Köpfe aus der Chronik. Wähle je Bild den passenden Spieler und ordne zu. Grüner Rahmen = bereits zugeordnet.
                </p>
              </CardHeader>
              <CardContent>
                {loadingPortraits ? (
                  <p className="text-sm text-rose-200/50">Laden...</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {portraits.map(p => {
                      const holder = users.find(u => u.photo_url === p.url);
                      return (
                        <div
                          key={p.nick}
                          data-testid={`portrait-card-${p.nick}`}
                          className={`rounded-xl p-2 border ${holder ? 'border-green-500/50 bg-green-900/10' : 'border-white/10 bg-white/5'}`}
                        >
                          <div className="h-28 flex items-center justify-center mb-2 rounded-lg bg-gradient-to-b from-white/5 to-transparent overflow-hidden">
                            <img src={p.url} alt={p.nick} className="max-h-28 object-contain" data-testid={`portrait-img-${p.nick}`} />
                          </div>
                          <div className="text-xs font-semibold text-center mb-1 truncate">{p.nick}</div>
                          <select
                            value={portraitPick[p.nick] || ''}
                            onChange={(e) => setPortraitPick(prev => ({ ...prev, [p.nick]: e.target.value }))}
                            data-testid={`portrait-select-${p.nick}`}
                            className="w-full bg-[#1c0f13] border border-white/15 rounded-md text-xs h-8 px-1 mb-1"
                          >
                            <option value="">Spieler wählen…</option>
                            {users.map(u => (
                              <option key={u.id} value={u.id}>{u.display_name || u.name || u.email}</option>
                            ))}
                          </select>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              onClick={() => assignPortrait(p.nick, p.url)}
                              disabled={savingPortrait === p.nick || !portraitPick[p.nick]}
                              className="flex-1 h-7 text-[11px] bg-rose-600 hover:bg-rose-700"
                              data-testid={`portrait-assign-${p.nick}`}
                            >
                              {savingPortrait === p.nick ? '…' : 'Zuordnen'}
                            </Button>
                            {holder && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => clearPortrait(holder.id)}
                                disabled={savingPortrait === 'clear-' + holder.id}
                                className="h-7 text-[11px] border-white/20 px-2"
                                data-testid={`portrait-clear-${p.nick}`}
                              >
                                <X size={12} />
                              </Button>
                            )}
                          </div>
                          {holder && (
                            <div className="text-[10px] text-green-400 text-center mt-1 truncate">
                              → {holder.display_name || holder.name}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SETTINGS TAB */}
          <TabsContent value="settings" className="space-y-4">
            <Card className="bg-[#12090c] border-white/10">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Settings size={16} />
                  Season Einstellungen
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                
                {/* COIN ECONOMY */}
                <div className="p-4 bg-amber-900/10 border border-amber-500/20 rounded-lg">
                  <h4 className="text-xs text-amber-400 font-bold uppercase tracking-wider mb-4 flex items-center gap-2">
                    🪙 COIN ECONOMY
                  </h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-xs text-rose-200/60 block mb-1">Startguthaben</label>
                      <Input
                        type="number"
                        value={seasonSettings.startingCoins}
                        onChange={(e) => setSeasonSettings(prev => ({ ...prev, startingCoins: parseInt(e.target.value) || 100 }))}
                        className="bg-white/5 border-white/10"
                      />
                      <p className="text-[10px] text-rose-200/30 mt-1">Einmalig pro Season</p>
                    </div>
                    <div>
                      <label className="text-xs text-rose-200/60 block mb-1">Richtig Erraten</label>
                      <Input
                        type="number"
                        value={seasonSettings.correctGuessCoins}
                        onChange={(e) => setSeasonSettings(prev => ({ ...prev, correctGuessCoins: parseInt(e.target.value) || 10 }))}
                        className="bg-white/5 border-white/10"
                      />
                      <p className="text-[10px] text-rose-200/30 mt-1">Pro WHO IS IT Guess</p>
                    </div>
                    <div>
                      <label className="text-xs text-rose-200/60 block mb-1">Challenge Bonus</label>
                      <Input
                        type="number"
                        value={seasonSettings.challengeWinCoins}
                        onChange={(e) => setSeasonSettings(prev => ({ ...prev, challengeWinCoins: parseInt(e.target.value) || 20 }))}
                        className="bg-white/5 border-white/10"
                      />
                      <p className="text-[10px] text-rose-200/30 mt-1">Bei SUCCESS (Phase B)</p>
                    </div>
                  </div>
                  <p className="text-[10px] text-rose-200/30 mt-3">
                    Hinweis: Für Probability Voting gibt es KEINE Coins. Coins nur für: Startguthaben, richtige WHO IS IT Guesses, Challenge Gewinne.
                  </p>
                </div>
                
                {/* BETTING LIMITS */}
                <div className="p-4 bg-blue-900/10 border border-blue-500/20 rounded-lg">
                  <h4 className="text-xs text-blue-400 font-bold uppercase tracking-wider mb-4 flex items-center gap-2">
                    📊 BETTING LIMITS
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div>
                      <label className="text-xs text-rose-200/60 block mb-1">Challenge Min</label>
                      <Input
                        type="number"
                        value={seasonSettings.challengeBetMin}
                        onChange={(e) => setSeasonSettings(prev => ({ ...prev, challengeBetMin: parseInt(e.target.value) || 1 }))}
                        className="bg-white/5 border-white/10"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-rose-200/60 block mb-1">Challenge Max</label>
                      <Input
                        type="number"
                        value={seasonSettings.challengeBetMax}
                        onChange={(e) => setSeasonSettings(prev => ({ ...prev, challengeBetMax: parseInt(e.target.value) || 500 }))}
                        className="bg-white/5 border-white/10"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-rose-200/60 block mb-1">Max % pro Challenge</label>
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        value={seasonSettings.challengeMaxStakePercent}
                        onChange={(e) => setSeasonSettings(prev => ({ ...prev, challengeMaxStakePercent: Math.min(100, Math.max(1, parseInt(e.target.value) || 50)) }))}
                        className="bg-white/5 border-white/10"
                      />
                      <p className="text-[10px] text-rose-200/30 mt-1">50% = max halbes Wallet</p>
                    </div>
                    <div>
                      <label className="text-xs text-rose-200/60 block mb-1">Prediction Min</label>
                      <Input
                        type="number"
                        value={seasonSettings.predictionBetMin}
                        onChange={(e) => setSeasonSettings(prev => ({ ...prev, predictionBetMin: parseInt(e.target.value) || 10 }))}
                        className="bg-white/5 border-white/10"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-rose-200/60 block mb-1">Prediction Max</label>
                      <Input
                        type="number"
                        value={seasonSettings.predictionBetMax}
                        onChange={(e) => setSeasonSettings(prev => ({ ...prev, predictionBetMax: parseInt(e.target.value) || 1000 }))}
                        className="bg-white/5 border-white/10"
                      />
                    </div>
                  </div>
                </div>
                
                {/* QUOTEN */}
                <div className="p-4 bg-purple-900/10 border border-purple-500/20 rounded-lg">
                  <h4 className="text-xs text-purple-400 font-bold uppercase tracking-wider mb-4 flex items-center gap-2">
                    🎲 QUOTEN
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="text-xs text-rose-200/60 block mb-1">Quote Floor (%)</label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={seasonSettings.quoteFloorProbability}
                        onChange={(e) => setSeasonSettings(prev => ({ ...prev, quoteFloorProbability: parseInt(e.target.value) || 1 }))}
                        className="bg-white/5 border-white/10"
                      />
                      <p className="text-[10px] text-rose-200/30 mt-1">Min % für Berechnung</p>
                    </div>
                    <div>
                      <label className="text-xs text-rose-200/60 block mb-1">Max Quote (x)</label>
                      <Input
                        type="number"
                        value={seasonSettings.maximumQuote}
                        onChange={(e) => setSeasonSettings(prev => ({ ...prev, maximumQuote: parseFloat(e.target.value) || 100 }))}
                        className="bg-white/5 border-white/10"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-rose-200/60 block mb-1">Challenge Odds Seed</label>
                      <Input
                        type="number"
                        value={seasonSettings.challengeOddsSeed}
                        onChange={(e) => setSeasonSettings(prev => ({ ...prev, challengeOddsSeed: parseInt(e.target.value) || 50 }))}
                        className="bg-white/5 border-white/10"
                      />
                      <p className="text-[10px] text-rose-200/30 mt-1">Start-Pool für Live-Odds</p>
                    </div>
                    <div>
                      <label className="text-xs text-rose-200/60 block mb-1">Challenge Max Quote</label>
                      <Input
                        type="number"
                        value={seasonSettings.challengeMaximumQuote}
                        onChange={(e) => setSeasonSettings(prev => ({ ...prev, challengeMaximumQuote: parseFloat(e.target.value) || 20 }))}
                        className="bg-white/5 border-white/10"
                      />
                    </div>
                  </div>
                </div>
                
                {/* TIMING */}
                <div className="p-4 bg-green-900/10 border border-green-500/20 rounded-lg">
                  <h4 className="text-xs text-green-400 font-bold uppercase tracking-wider mb-4 flex items-center gap-2">
                    ⏱️ TIMING
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-rose-200/60 block mb-1">WHO IS IT Zeit (Sek.)</label>
                      <Input
                        type="number"
                        min="5"
                        max="180"
                        value={seasonSettings.guessSeconds}
                        onChange={(e) => {
                          const val = Math.min(180, Math.max(5, parseInt(e.target.value) || 30));
                          setSeasonSettings(prev => ({ ...prev, guessSeconds: val }));
                        }}
                        className="bg-white/5 border-white/10"
                      />
                      <p className="text-[10px] text-rose-200/30 mt-1">Min 5, Max 180 Sekunden (3 Min)</p>
                    </div>
                    <div>
                      <label className="text-xs text-rose-200/60 block mb-1">Challenge Betting (Sek.)</label>
                      <Input
                        type="number"
                        value={seasonSettings.challengeBettingSeconds}
                        onChange={(e) => setSeasonSettings(prev => ({ ...prev, challengeBettingSeconds: parseInt(e.target.value) || 600 }))}
                        className="bg-white/5 border-white/10"
                      />
                      <p className="text-[10px] text-rose-200/30 mt-1">Default: 600 (10 Min)</p>
                    </div>
                  </div>
                  <p className="text-xs text-amber-400/70 mt-3">
                    ℹ️ Probability Voting hat KEIN Zeitlimit - endet automatisch wenn alle abgestimmt haben.
                  </p>
                </div>
                
                <Button onClick={saveSeasonSettings} disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700">
                  <Save size={14} className="mr-2" /> Einstellungen speichern
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// Import Verification Form Component
function ImportVerificationForm({ importItem, suggestedUser, users, siblingImports = [], onVerify, onReject, saving }) {
  const [selectedUserId, setSelectedUserId] = useState(suggestedUser?.id || '');
  const [onCourtText, setOnCourtText] = useState(importItem.onCourtText || '');
  const [offCourtText, setOffCourtText] = useState(importItem.offCourtText || '');
  const [mergedIds, setMergedIds] = useState([]);
  
  const swapTexts = () => {
    setOnCourtText(offCourtText);
    setOffCourtText(onCourtText);
  };
  
  const takeText = (sib, text, target) => {
    if (!text?.trim()) return;
    if (target === 'on') setOnCourtText(text);
    else setOffCourtText(text);
    setMergedIds(prev => prev.includes(sib.id) ? prev : [...prev, sib.id]);
  };
  
  const sortedUsers = [...users].sort((a, b) =>
    (a.display_name || a.name || a.email || '').localeCompare(b.display_name || b.name || b.email || '', 'de')
  );
  
  return (
    <div className="mt-4 pt-4 border-t border-white/10 space-y-4" data-testid="import-verification-form">
      {/* User Selection - This assigns the AUTHOR (person who submitted, from Column B) */}
      <div>
        <label className="text-xs text-rose-200/60 block mb-2">
          <span className="font-bold text-amber-400">VON WEM?</span> → Zuordnung zum Autor (Spalte B)
        </label>
        <div className="flex gap-2">
          <select
            data-testid="import-user-select"
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            className="flex-1 p-2 bg-[#12090c] border border-white/10 rounded-lg text-sm"
          >
            <option value="">User auswählen...</option>
            {sortedUsers.map(u => (
              <option key={u.id} value={u.id}>
                {u.display_name || u.name || u.email}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Sibling imports from the same author → merge */}
      {siblingImports.length > 0 && (
        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 space-y-2" data-testid="import-merge-box">
          <p className="text-xs text-amber-300 font-medium">
            ⚠️ {siblingImports.length} weiterer Eintrag von „{importItem.formName}“ – Text übernehmen und zusammenführen:
          </p>
          {siblingImports.map(sib => (
            <div key={sib.id} className="text-xs space-y-1">
              {[['on', sib.onCourtText, 'ON'], ['off', sib.offCourtText, 'OFF']].map(([src, text, label]) => text?.trim() ? (
                <div key={src} className="flex items-center gap-2 bg-black/30 rounded p-2">
                  <span className={`shrink-0 font-bold ${src === 'on' ? 'text-blue-400' : 'text-purple-400'}`}>{label}:</span>
                  <span className="flex-1 text-rose-100/80 truncate" title={text}>{text}</span>
                  <button type="button" data-testid={`merge-${src}-to-on`} onClick={() => takeText(sib, text, 'on')} className="shrink-0 px-2 py-1 rounded bg-blue-600/40 hover:bg-blue-600/60">→ ON</button>
                  <button type="button" data-testid={`merge-${src}-to-off`} onClick={() => takeText(sib, text, 'off')} className="shrink-0 px-2 py-1 rounded bg-purple-600/40 hover:bg-purple-600/60">→ OFF</button>
                </div>
              ) : null)}
            </div>
          ))}
          {mergedIds.length > 0 && (
            <p className="text-xs text-green-400" data-testid="merge-info">
              ✓ {mergedIds.length} Eintrag wird beim Verifizieren mit zusammengeführt (verschwindet aus der Liste)
            </p>
          )}
        </div>
      )}
      
      {/* Predictions Edit */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-blue-400 block mb-1">
            ON-COURT {!onCourtText.trim() && <span className="text-rose-300/60">(leer)</span>}
          </label>
          <textarea
            data-testid="import-on-court-text"
            value={onCourtText}
            onChange={(e) => setOnCourtText(e.target.value)}
            className="w-full p-2 bg-[#12090c] border border-white/10 rounded-lg text-sm min-h-[60px] resize-none"
          />
        </div>
        <div>
          <label className="text-xs text-purple-400 block mb-1">
            OFF-COURT {!offCourtText.trim() && <span className="text-rose-300/60">(leer)</span>}
          </label>
          <textarea
            data-testid="import-off-court-text"
            value={offCourtText}
            onChange={(e) => setOffCourtText(e.target.value)}
            className="w-full p-2 bg-[#12090c] border border-white/10 rounded-lg text-sm min-h-[60px] resize-none"
          />
        </div>
      </div>
      <button type="button" data-testid="import-swap-texts" onClick={swapTexts} className="text-xs text-rose-200/70 hover:text-rose-50 underline">
        ⇄ ON und OFF tauschen
      </button>
      
      {/* Actions */}
      <div className="flex gap-2">
        <Button
          data-testid="import-verify-btn"
          onClick={() => onVerify(importItem, selectedUserId, onCourtText, offCourtText, mergedIds)}
          disabled={saving || !selectedUserId || (!onCourtText.trim() && !offCourtText.trim())}
          className="flex-1 bg-green-600 hover:bg-green-700"
        >
          <CheckCircle size={14} className="mr-1" />
          VERIFIZIEREN{mergedIds.length > 0 ? ` (+${mergedIds.length} zusammengeführt)` : ''}
        </Button>
        <Button
          data-testid="import-reject-btn"
          onClick={() => onReject(importItem)}
          disabled={saving}
          variant="outline"
          className="bg-red-600/20 border-red-500/50 hover:bg-red-600/30"
        >
          <XCircle size={14} className="mr-1" />
          Ablehnen / Entfernen
        </Button>
      </div>
    </div>
  );
}


// Economy Overview Tab Component
function EconomyOverviewTab() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userLedger, setUserLedger] = useState(null);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjusting, setAdjusting] = useState(false);
  const [adjustMsg, setAdjustMsg] = useState('');

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const data = await apiCall('/api/economy/admin/overview');
        setOverview(data);
      } catch (err) {
        console.error('Failed to fetch economy overview:', err);
      }
      setLoading(false);
    };
    fetchOverview();
  }, []);

  const fetchUserLedger = async (userId) => {
    try {
      const data = await apiCall(`/api/economy/admin/user/${userId}/ledger`);
      setUserLedger(data);
      setSelectedUser(userId);
      setAdjustAmount(''); setAdjustReason(''); setAdjustMsg('');
    } catch (err) {
      console.error('Failed to fetch user ledger:', err);
    }
  };

  const applyAdjust = async (sign) => {
    const val = parseInt(adjustAmount, 10);
    if (!val || val <= 0) { setAdjustMsg('Bitte gültige Anzahl eingeben'); return; }
    setAdjusting(true); setAdjustMsg('');
    try {
      const res = await apiCall('/api/economy/admin/adjust-wallet', 'POST', {
        user_id: selectedUser,
        delta: sign * val,
        reason: adjustReason.trim() || 'Manuelle Korrektur durch Spielleiter',
      });
      setAdjustAmount(''); setAdjustReason('');
      await fetchUserLedger(selectedUser);
      setAdjustMsg(`✅ Neuer Kontostand: ${res.balance} 🪙`);
    } catch (err) {
      setAdjustMsg(`❌ ${err.message || 'Fehler'}`);
    }
    setAdjusting(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!overview) {
    return <div className="text-center py-8 text-white/50">Economy-Daten nicht verfügbar</div>;
  }

  return (
    <div className="space-y-4">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-[#12090c] border-white/10">
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-yellow-400">{overview.totalWallets}</div>
            <div className="text-xs text-white/50">Wallets</div>
          </CardContent>
        </Card>
        <Card className="bg-[#12090c] border-white/10">
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-green-400">{overview.totalCoinsInSystem.toLocaleString('de-DE')}</div>
            <div className="text-xs text-white/50">Coins Gesamt</div>
          </CardContent>
        </Card>
        <Card className="bg-[#12090c] border-white/10">
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-orange-400">{overview.totalReserved.toLocaleString('de-DE')}</div>
            <div className="text-xs text-white/50">Reserviert</div>
          </CardContent>
        </Card>
        <Card className="bg-[#12090c] border-white/10">
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-blue-400">{overview.openBets} / {overview.settledBets}</div>
            <div className="text-xs text-white/50">Offen / Settled</div>
          </CardContent>
        </Card>
      </div>

      {/* Leaderboard */}
      <Card className="bg-[#12090c] border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            🏆 Leaderboard
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {overview.leaderboard.map((entry) => (
              <div
                key={entry.userId}
                onClick={() => fetchUserLedger(entry.userId)}
                className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 cursor-pointer transition-colors"
              >
                <div className="w-8 text-center font-bold text-white/60">
                  {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : `${entry.rank}.`}
                </div>
                <div className="flex-1 truncate text-white">{entry.displayName}</div>
                <div className="font-mono text-yellow-400">
                  {entry.balance.toLocaleString('de-DE')} 🪙
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* User Ledger Modal */}
      {selectedUser && userLedger && (
        <Card className="bg-[#12090c] border-yellow-500/30">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">
                📒 {userLedger.displayName} - Ledger
              </CardTitle>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setSelectedUser(null); setUserLedger(null); }}
              >
                <X size={14} />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Wallet Summary */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-white/5 rounded-lg p-2">
                <div className="text-lg font-bold text-yellow-400">{userLedger.wallet.balance}</div>
                <div className="text-xs text-white/50">Balance</div>
              </div>
              <div className="bg-white/5 rounded-lg p-2">
                <div className="text-lg font-bold text-orange-400">{userLedger.wallet.reserved}</div>
                <div className="text-xs text-white/50">Reserviert</div>
              </div>
              <div className="bg-white/5 rounded-lg p-2">
                <div className="text-lg font-bold text-green-400">{userLedger.wallet.available}</div>
                <div className="text-xs text-white/50">Verfügbar</div>
              </div>
            </div>

            {/* Coins hinzufügen / abziehen */}
            <div className="bg-white/5 rounded-lg p-3 space-y-2 border border-white/10">
              <h4 className="text-xs text-white/70 font-semibold">Coins hinzufügen / abziehen</h4>
              <div className="flex gap-2">
                <input
                  type="number" min="1"
                  value={adjustAmount}
                  onChange={(e) => setAdjustAmount(e.target.value)}
                  placeholder="Anzahl"
                  className="w-24 bg-[#0d0409] border border-white/20 rounded px-2 py-1.5 text-sm"
                  data-testid="adjust-amount"
                />
                <input
                  type="text"
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                  placeholder="Grund (z.B. Bonus für Anmeldung)"
                  className="flex-1 bg-[#0d0409] border border-white/20 rounded px-2 py-1.5 text-sm"
                  data-testid="adjust-reason"
                />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => applyAdjust(1)} disabled={adjusting}
                  className="flex-1 h-8 text-xs bg-green-600 hover:bg-green-700" data-testid="adjust-add">
                  <Plus size={12} className="mr-1" /> Gutschreiben
                </Button>
                <Button size="sm" onClick={() => applyAdjust(-1)} disabled={adjusting}
                  className="flex-1 h-8 text-xs bg-red-600 hover:bg-red-700" data-testid="adjust-deduct">
                  <X size={12} className="mr-1" /> Abziehen
                </Button>
              </div>
              {adjustMsg && <p className="text-xs text-white/70" data-testid="adjust-msg">{adjustMsg}</p>}
            </div>

            {/* Transactions */}
            <div>
              <h4 className="text-xs text-white/60 mb-2">Letzte Transaktionen</h4>
              <div className="space-y-1 max-h-[200px] overflow-y-auto">
                {userLedger.transactions.slice(0, 20).map((txn, i) => (
                  <div key={txn.id || i} className="flex items-center justify-between px-2 py-1 bg-white/5 rounded text-sm">
                    <span className="text-white/70 truncate flex-1">{txn.reason || txn.type}</span>
                    <span className={txn.amount >= 0 ? 'text-green-400' : 'text-red-400'}>
                      {txn.amount >= 0 ? '+' : ''}{txn.amount}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Bets */}
            <div>
              <h4 className="text-xs text-white/60 mb-2">Wetten</h4>
              <div className="space-y-1 max-h-[150px] overflow-y-auto">
                {userLedger.bets.map((bet, i) => (
                  <div key={bet.id || i} className="flex items-center justify-between px-2 py-1 bg-white/5 rounded text-sm">
                    <span className={bet.side === 'YES' ? 'text-green-400' : 'text-red-400'}>{bet.side}</span>
                    <span className="text-white/70">{bet.amount} @ x{bet.oddsAtPlacement}</span>
                    <span className={
                      bet.status === 'WON' ? 'text-green-400' :
                      bet.status === 'LOST' ? 'text-red-400' :
                      bet.status === 'VOID' ? 'text-gray-400' : 'text-yellow-400'
                    }>
                      {bet.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Season Market Admin Tab Component
function SeasonMarketAdminTab() {
  const [marketStatus, setMarketStatus] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [confirmationTarget, setConfirmationTarget] = useState(null); // Changed from settlementTarget

  const fetchMarketData = useCallback(async () => {
    try {
      const [statusData, predictionsData] = await Promise.all([
        apiCall('/api/predictions-market/status?season_id=2026-27'),
        apiCall('/api/predictions-market/predictions?season_id=2026-27'),
      ]);
      setMarketStatus(statusData);
      setPredictions(predictionsData.predictions || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch market data:', err);
      setError(err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchMarketData();
  }, [fetchMarketData]);

  const handleOpenMarket = async () => {
    if (!confirm('Markt ÖFFNEN? Dadurch werden:\n\n1. Alle Quoten eingefroren\n2. Autoren-Freebets erstellt\n\nFortfahren?')) return;
    
    setActionLoading(true);
    setError(null);
    try {
      const result = await apiCall('/api/predictions-market/admin/open-market', 'POST', { seasonId: '2026-27' });
      setSuccessMessage(`Markt geöffnet! ${result.oddsFrozenCount} Quoten eingefroren, ${result.freebetsCreatedCount} Freebets erstellt.`);
      fetchMarketData();
    } catch (err) {
      setError(err.message);
    }
    setActionLoading(false);
  };

  const handleCloseMarket = async () => {
    if (!confirm('Markt SCHLIESSEN? Keine neuen Wetten mehr möglich.')) return;
    
    setActionLoading(true);
    setError(null);
    try {
      await apiCall('/api/predictions-market/admin/close-market', 'POST', { seasonId: '2026-27' });
      setSuccessMessage('Markt geschlossen.');
      fetchMarketData();
    } catch (err) {
      setError(err.message);
    }
    setActionLoading(false);
  };

  const handleAdminConfirm = async (predictionId, result) => {
    const confirmMsg = `Als ${result} bestätigen?\n\n${
      result === 'SUCCESS' ? 'JA ist eingetroffen' : 
      result === 'FAIL' ? 'NEIN - nicht eingetroffen' : 
      'VOID - Wetten werden erstattet'
    }\n\n⚠️ Dual Confirmation: Settlement erfolgt nur, wenn der AUTOR das gleiche Ergebnis bestätigt.`;
    
    if (!confirm(confirmMsg)) return;
    
    setActionLoading(true);
    setError(null);
    try {
      const res = await apiCall(`/api/predictions-market/${predictionId}/admin-confirm`, 'POST', { result });
      
      if (res.status === 'MATCHED') {
        setSuccessMessage(`✅ Settlement ausgelöst! ${res.settledWagers || 0} Wetten, ${(res.totalPayouts || 0).toFixed(0)} Coins ausgezahlt.`);
      } else if (res.status === 'DISPUTED') {
        setSuccessMessage(`⚠️ DISPUTED: Admin sagt ${res.adminResult}, Autor sagt ${res.authorResult}. Manuelle Klärung erforderlich.`);
      } else if (res.status === 'ADMIN_CONFIRMED') {
        setSuccessMessage(`🕐 Admin-Bestätigung gespeichert (${result}). Warte auf Autor-Bestätigung.`);
      } else if (res.idempotent) {
        setSuccessMessage(`ℹ️ ${res.message || 'Bereits bestätigt.'}`);
      }
      
      setConfirmationTarget(null);
      fetchMarketData();
    } catch (err) {
      setError(err.message);
    }
    setActionLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-green-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Status Messages */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-300 text-sm">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-3 text-green-300 text-sm">
          {successMessage}
          <button onClick={() => setSuccessMessage(null)} className="ml-2 text-white/50 hover:text-white">×</button>
        </div>
      )}

      {/* Market Status Card */}
      <Card className="bg-[#12090c] border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            📊 Season Market Status
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status Badge */}
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${
            marketStatus?.marketStatus === 'OPEN' 
              ? 'bg-green-500/20 text-green-400 border border-green-500/30'
              : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
          }`}>
            {marketStatus?.marketStatus === 'OPEN' ? '🟢 MARKT OFFEN' : '🔒 MARKT GESCHLOSSEN'}
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white/5 rounded-lg p-3 text-center">
              <div className="text-xl font-bold text-blue-400">{marketStatus?.totalPredictions || 0}</div>
              <div className="text-xs text-white/50">Predictions</div>
            </div>
            <div className="bg-white/5 rounded-lg p-3 text-center">
              <div className="text-xl font-bold text-green-400">{marketStatus?.quotedPredictions || 0}</div>
              <div className="text-xs text-white/50">Mit Quote</div>
            </div>
            <div className="bg-white/5 rounded-lg p-3 text-center">
              <div className="text-xl font-bold text-yellow-400">{marketStatus?.openWagers || 0}</div>
              <div className="text-xs text-white/50">Offene Wetten</div>
            </div>
            <div className="bg-white/5 rounded-lg p-3 text-center">
              <div className="text-xl font-bold text-purple-400">{marketStatus?.settledPredictions || 0}</div>
              <div className="text-xs text-white/50">Abgerechnet</div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            {marketStatus?.marketStatus !== 'OPEN' ? (
              <Button
                onClick={handleOpenMarket}
                disabled={actionLoading}
                className="bg-green-600 hover:bg-green-700"
              >
                {actionLoading ? '...' : '🚀 Markt ÖFFNEN'}
              </Button>
            ) : (
              <Button
                onClick={handleCloseMarket}
                disabled={actionLoading}
                className="bg-yellow-600 hover:bg-yellow-700"
              >
                {actionLoading ? '...' : '🔒 Markt SCHLIESSEN'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Predictions List with Settlement */}
      <Card className="bg-[#12090c] border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            📋 Predictions & Settlement
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {predictions.map((pred) => (
              <div
                key={pred.predictionId}
                className={`p-3 rounded-lg border ${
                  pred.settlementResult 
                    ? 'bg-white/5 border-white/10 opacity-60'
                    : 'bg-white/5 border-white/10'
                }`}
              >
                {/* Prediction Header */}
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        pred.type === 'on_court' 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        {pred.type === 'on_court' ? 'ON-COURT' : 'OFF-COURT'}
                      </span>
                      <span className="text-xs text-white/50">von {pred.ownerDisplayName}</span>
                    </div>
                    <p className="text-sm text-white">{pred.predictionText}</p>
                  </div>
                  
                  {/* Odds */}
                  <div className="text-right text-xs">
                    <div className="text-green-400">JA x{pred.yesOdds?.toFixed(2)}</div>
                    <div className="text-red-400">NEIN x{pred.noOdds?.toFixed(2)}</div>
                  </div>
                </div>

                {/* Settlement Status or Actions */}
                {pred.settlementResult ? (
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded text-sm font-medium ${
                    pred.settlementResult === 'SUCCESS' 
                      ? 'bg-green-500/20 text-green-400'
                      : pred.settlementResult === 'FAIL'
                        ? 'bg-red-500/20 text-red-400'
                        : 'bg-gray-500/20 text-gray-400'
                  }`}>
                    {pred.settlementResult === 'SUCCESS' ? '✓ EINGETROFFEN' :
                     pred.settlementResult === 'FAIL' ? '✗ NICHT EINGETROFFEN' : '○ VOID'}
                  </div>
                ) : pred.resolutionStatus === 'DISPUTED' ? (
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded text-sm font-medium bg-yellow-500/20 text-yellow-400">
                    ⚠️ DISPUTED - Klärung erforderlich
                  </div>
                ) : confirmationTarget === pred.predictionId ? (
                  <div className="space-y-2">
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        size="sm"
                        onClick={() => handleAdminConfirm(pred.predictionId, 'SUCCESS')}
                        disabled={actionLoading}
                        className="bg-green-600 hover:bg-green-700 text-xs"
                      >
                        ✓ SUCCESS
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleAdminConfirm(pred.predictionId, 'FAIL')}
                        disabled={actionLoading}
                        className="bg-red-600 hover:bg-red-700 text-xs"
                      >
                        ✗ FAIL
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleAdminConfirm(pred.predictionId, 'VOID')}
                        disabled={actionLoading}
                        className="bg-gray-600 hover:bg-gray-700 text-xs"
                      >
                        ○ VOID
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setConfirmationTarget(null)}
                        className="text-xs"
                      >
                        Abbrechen
                      </Button>
                    </div>
                    {/* Show current confirmation status */}
                    {(pred.authorConfirmation || pred.adminConfirmation) && (
                      <div className="text-xs text-white/50">
                        {pred.authorConfirmation && (
                          <span className="mr-2">
                            👤 Autor: <span className={pred.authorConfirmation.result === 'SUCCESS' ? 'text-green-400' : pred.authorConfirmation.result === 'FAIL' ? 'text-red-400' : 'text-gray-400'}>
                              {pred.authorConfirmation.result}
                            </span>
                          </span>
                        )}
                        {pred.adminConfirmation && (
                          <span>
                            🔑 Admin: <span className={pred.adminConfirmation.result === 'SUCCESS' ? 'text-green-400' : pred.adminConfirmation.result === 'FAIL' ? 'text-red-400' : 'text-gray-400'}>
                              {pred.adminConfirmation.result}
                            </span>
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setConfirmationTarget(pred.predictionId)}
                      className="text-xs border-white/20 hover:bg-white/10"
                    >
                      Admin Bestätigung...
                    </Button>
                    {/* Show status badges */}
                    {pred.resolutionStatus === 'AUTHOR_CONFIRMED' && (
                      <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">
                        👤 Autor: {pred.authorConfirmation?.result}
                      </span>
                    )}
                    {pred.resolutionStatus === 'ADMIN_CONFIRMED' && (
                      <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded">
                        🔑 Admin: {pred.adminConfirmation?.result}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
            
            {predictions.length === 0 && (
              <div className="text-center py-8 text-white/50">
                Keine Predictions gefunden
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
